import styled, { css } from 'styled-components';
import { DatepickerPosition } from './enums';
import { StyledDatepickerProps } from './types';

export const StyledDatepicker = styled.div<StyledDatepickerProps>`
  position: relative;
  display: flex;
  align-items: flex-start;
  ${({ position }) =>
    position === DatepickerPosition.Right &&
    css`
      justify-content: flex-end;
    `};
  .datepicker {
    position: absolute;
    top: 100%;
    margin-top: 0.5rem;
    flex-direction: row-reverse;
    background-color: var(--surface);
    overflow: hidden;
    box-shadow: ${({ theme }) => theme.shadows.mid};
    border-radius: 1rem;
    z-index: ${({ theme }) => theme.zindexes.autocomplete};
    ${({ position, theme }) =>
      theme.direction === 'rtl'
        ? css`
            ${position === DatepickerPosition.Left
              ? css`
                  right: 0;
                `
              : css`
                  left: 0;
                `}
          `
        : css`
            ${position === DatepickerPosition.Left
              ? css`
                  left: 0;
                `
              : css`
                  right: 0;
                `}
          `};
  }
  .rdrMonthAndYearWrapper {
    border-bottom: 1px solid var(--line);
    padding: 1rem;
    height: auto;
  }
  .rdrNextPrevButton {
    margin: 0;
    border-radius: 100%;
    background-color: transparent;
    border: 2px solid var(--ink);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .rdrNextButton i {
    margin: 0;
  }
  .rdrMonth:not(:last-child) {
    ${({ theme }) =>
      theme.direction === 'rtl'
        ? css`
            border-left: 1px solid var(--line);
          `
        : css`
            border-right: 1px solid var(--line);
          `};
  }
  .rdrDefinedRangesWrapper {
    flex-shrink: 0;
    border-right: 0;
    ${({ theme }) =>
      theme.direction === 'rtl'
        ? css`
            border-right: 1px solid var(--line);
          `
        : css`
            border-left: 1px solid var(--line);
          `};

    width: auto;
    padding: 1rem 0;
  }
  .rdrStaticRange {
    border-bottom: 0;
  }
  .rdrStaticRangeLabel {
    font-family: var(--font-sans);
    font-weight: var(--weight-medium);
    font-size: var(--t-sm);
    padding: 0.75rem 1.5rem;
    color: var(--ink);
    white-space: nowrap;
  }
  .rdrMonthAndYearPickers select {
    border-radius: 0.5rem;
    background-color: var(--surface);
    border: 1px solid var(--line);
    height: 2.5rem;
    padding: 0.625rem 1.625rem 0.625rem 0.875rem;
    font-family: var(--font-sans);
    font-weight: var(--weight-medium);
    font-size: var(--t-sm);
  }
  .rdrInputRanges {
    display: none;
  }
  .rdrDayToday .rdrDayNumber span:after,
  .rdrDayToday:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span:after,
  .rdrDayToday:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span:after,
  .rdrDayToday:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span:after,
  .rdrDayToday:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span:after {
    display: none;
  }
  .rdrMonth {
    width: 20.5rem;
    padding: 1rem 1rem calc(1rem - 0.375rem);
  }
  .rdrDay {
    height: auto;
    margin-bottom: 0.375rem;
    &::after {
      content: '';
      padding-top: 100%;
      display: block;
    }
  }

  .rdrSelected,
  .rdrInRange,
  .rdrStartEdge,
  .rdrEndEdge {
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  .rdrStartEdge,
  .rdrEndEdge {
    background-color: transparent;
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: currentColor;
    }
    &::before {
      opacity: 0.2;
      border-radius: inherit;
    }
    &::after {
      border-radius: 100%;
    }
  }

  .rdrStartEdge.rdrEndEdge {
    border-radius: 100%;
  }

  .rdrInRange {
    opacity: 0.2;
  }

  .rdrEndEdge {
    ${({ theme }) =>
      theme.direction === 'rtl'
        ? css`
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-top-left-radius: 100%;
            border-bottom-left-radius: 100%;
          `
        : css`
            border-top-right-radius: 100%;
            border-bottom-right-radius: 100%;
          `};
  }

  .rdrStartEdge {
    ${({ theme }) =>
      theme.direction === 'rtl'
        ? css`
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-top-right-radius: 100%;
            border-bottom-right-radius: 100%;
          `
        : css`
            border-top-left-radius: 100%;
            border-bottom-left-radius: 100%;
          `};
  }

  .rdrDayEndOfMonth .rdrInRange,
  .rdrDayEndOfWeek .rdrInRange {
    ${({ theme }) =>
      theme.direction === 'rtl'
        ? css`
            border-top-left-radius: 100%;
            border-bottom-left-radius: 100%;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          `
        : css`
            border-top-right-radius: 100%;
            border-bottom-right-radius: 100%;
          `};
  }

  .rdrDayEndOfMonth .rdrStartEdge,
  .rdrDayEndOfWeek .rdrStartEdge {
    ${({ theme }) =>
      theme.direction === 'rtl'
        ? css`
            border-top-left-radius: 100%;
            border-bottom-left-radius: 100%;
            border-top-right-radius: 100%;
            border-bottom-right-radius: 100%;
          `
        : css`
            border-top-right-radius: 100%;
            border-bottom-right-radius: 100%;
          `};
  }

  .rdrDayStartOfMonth .rdrInRange,
  .rdrDayStartOfWeek .rdrInRange {
    ${({ theme }) =>
      theme.direction === 'rtl'
        ? css`
            border-top-right-radius: 100%;
            border-bottom-right-radius: 100%;
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          `
        : css`
            border-top-left-radius: 100%;
            border-bottom-left-radius: 100%;
            left: 2px;
          `};
  }

  .rdrDayStartOfMonth .rdrEndEdge,
  .rdrDayStartOfWeek .rdrEndEdge {
    ${({ theme }) =>
      theme.direction === 'rtl'
        ? css`
            border-top-right-radius: 100%;
            border-bottom-right-radius: 100%;
            border-top-left-radius: 100%;
            border-bottom-left-radius: 100%;
          `
        : css`
            border-top-left-radius: 100%;
            border-bottom-left-radius: 100%;
          `};
  }

  .rdrMonthName,
  .rdrWeekDays {
    margin-bottom: 1rem;
  }

  .rdrMonthName {
    color: var(--ink);
  }

  .rdrWeekDays {
    color: rgba(0, 0, 0, 0.54);
  }

  .rdrMonthName,
  .rdrWeekDay,
  .rdrDayNumber {
    font-family: var(--font-sans);
    font-weight: var(--weight-medium);
    font-size: var(--t-sm);
  }

  .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
  .rdrDayNumber span {
    color: var(--ink);
  }

  .rdrDayPassive .rdrDayNumber span {
    color: rgba(0, 0, 0, 0.38);
  }
`;

export const StyledDatepickerButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 10px 0 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  font: inherit;
  font-size: var(--t-sm);
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  transition: border-color var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out);

  &:hover:not(:disabled) { border-color: var(--muted-3); }
  &[aria-expanded='true'] {
    border-color: var(--brand-green);
    box-shadow: 0 0 0 3px rgba(118, 177, 89, 0.15);
  }
  &:disabled { pointer-events: none; opacity: 0.45; }

  .fc-icon {
    display: flex; align-items: center;
    color: var(--muted); flex-shrink: 0;
    svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
  }
  .fc-label {
    display: flex; align-items: center; gap: 4px;
    flex: 1 1 auto;
  }
  .fc-chevron {
    display: flex; align-items: center;
    color: var(--muted-2); flex-shrink: 0; margin-left: 2px;
    svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
  }
`;

export const StyledDatepickerButtonDecor = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  ${({ theme }) =>
    theme.direction === 'rtl'
      ? css`
          padding-right: 0.875rem;
        `
      : css`
          padding-left: 0.875rem;
        `};
  svg {
    height: 1em;
    width: 1em;
    fill: none;
    stroke: currentColor;
    font-size: 1rem;
  }
`;

export const StyledDatepickerDecor = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  color: var(--brand-green);
  padding: 0 0.625rem;
  svg {
    height: 1em;
    width: 1em;
    fill: currentColor;
    font-size: 0.75rem;
    ${({ theme }) =>
      theme.direction === 'rtl' &&
      css`
        transform: rotate(180deg);
      `};
  }
`;
